import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { SETTINGS_PATH_PLANTAFEL } from '../../utils/settingsRoutes';
import { PLANTAFEL_GHOST_ICON_TRIGGER_CLASS } from './plantafelToolbarStyles';
import {
  normalizePlantafelDisplaySettings,
  type PlantafelDisplaySettings,
} from '../../utils/plantafelSettings';
import { PlantafelDisplaySettingsForm } from './PlantafelWeekdaySettingsForm';
import { fetchMe, getCachedCapabilities } from '../../lib/meClient';

export function PlantafelSettingsMenu({
  instancePrefix,
  displaySettings,
  onSaved,
}: {
  instancePrefix: string;
  displaySettings: PlantafelDisplaySettings;
  onSaved: (next: PlantafelDisplaySettings) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(displaySettings);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedHint, setSavedHint] = React.useState(false);
  const [canOpenOrgSettings, setCanOpenOrgSettings] = React.useState(
    () => getCachedCapabilities()?.['web.einstellungen.view'] === true,
  );
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled) return;
      setCanOpenOrgSettings(me?.capabilities?.['web.einstellungen.view'] === true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setDraft(displaySettings);
    setError(null);
    setSavedHint(false);
  }, [open, displaySettings]);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const width = 300;
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      setPanelPos({ top: rect.bottom + 6, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedHint(false);
    try {
      const res = await fetch('/api/settings/plantafel', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          visible_weekdays: draft.visibleWeekdays,
          day_hour_start: draft.dayHours.hourStart,
          day_hour_end: draft.dayHours.hourEnd,
          default_activity_duration_minutes: draft.defaultActivityDurationMinutes,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Speichern fehlgeschlagen.');
      }
      const data = (await res.json()) as {
        visible_weekdays?: unknown;
        day_hour_start?: unknown;
        day_hour_end?: unknown;
        default_activity_duration_minutes?: unknown;
      };
      const next = normalizePlantafelDisplaySettings(data);
      onSaved(next);
      setSavedHint(true);
      window.setTimeout(() => setSavedHint(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            className="fixed z-[80] w-[300px] rounded-lg border border-app-border bg-app-surface p-3 shadow-lg"
            style={{ top: panelPos.top, left: panelPos.left }}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-app-text">Plantafel</span>
              {canOpenOrgSettings ? (
                <Link
                  to={`${instancePrefix}${SETTINGS_PATH_PLANTAFEL}`}
                  className="text-xs font-medium text-pipedrive-blue hover:underline"
                  onClick={() => setOpen(false)}
                >
                  Alle Einstellungen
                </Link>
              ) : null}
            </div>
            <PlantafelDisplaySettingsForm
              weekdays={draft.visibleWeekdays}
              onWeekdaysChange={(visibleWeekdays) =>
                setDraft((prev) => ({ ...prev, visibleWeekdays }))
              }
              dayHours={draft.dayHours}
              onDayHoursChange={(dayHours) => setDraft((prev) => ({ ...prev, dayHours }))}
              defaultActivityDurationMinutes={draft.defaultActivityDurationMinutes}
              onDefaultActivityDurationMinutesChange={(defaultActivityDurationMinutes) =>
                setDraft((prev) => ({ ...prev, defaultActivityDurationMinutes }))
              }
              disabled={saving}
            />
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="rounded-md bg-pipedrive-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              {savedHint && <span className="text-xs text-emerald-600">Gespeichert.</span>}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={PLANTAFEL_GHOST_ICON_TRIGGER_CLASS}
        title="Plantafel-Einstellungen"
        aria-label="Plantafel-Einstellungen"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Settings className="h-4 w-4" />
      </button>
      {panel}
    </>
  );
}
